/* Built In Imports */
import NextLink from 'next/link';
import { memo, useEffect, useState } from 'react';
/* External Imports */
import { ChevronLeftIcon, ChevronRightIcon } from '@chakra-ui/icons';
import { Box, Text, Flex, Icon, Image, Link } from '@chakra-ui/react';
import { render } from 'datocms-structured-text-to-html-string';
import { nanoid } from 'nanoid';
import { useMediaQuery } from 'react-responsive';
import Slider from 'react-slick';

/* Internal Imports */
import StructuredTextParser from '@moms/Utility/StructuredTextParser';
import { refineUrlDomain } from '@moms/Utility/utils';
import config from '@config';
/* Components */
/* Styles */
import RescPrac from '@moms/styleData/RescPrac';
/* Services */

/**
 *
 * @param {Object} sectionContent
 * @param {String} region
 * @param {String} lang
 * @returns
 */

const MainHeading = ({ region, lang, sectionContent }) => {
  const content = sectionContent[0] || {};
  const title = content.titleText;
  const subtext = content.additionalTextLine;

  return (
    <Box
      as="h1"
      display={{ base: 'block', md: 'flex' }}
      textAlign="center"
      justifyContent="center"
      mb={{ base: '10px', md: '30px' }}
      gap=".8rem"
      pt="60px"
      pb="60px"
      {...RescPrac?.[lang]?.title}
      bgImage={`${config.imgPath}/d/46272/1750293075-bg_researchpractice.jpg`}
      bgRepeat={'no-repeat'}
      bgSize={'cover'}
      // background={`linear-gradient(0deg, rgba(0, 0, 0, 0) 0%, rgba(0, 0, 0, 0) 25%, rgba(0, 0, 0, 0) 76%, #000000 100%)`}
    >
      <Text
        as="span"
        color="#EC2224"
        fontStyle={lang === 'en' ? 'italic' : 'inherit'}
      >
        {title}
      </Text>
      <Text
        as="span"
        color="#FFFFFF"
        fontStyle={lang === 'en' ? 'normal' : 'inherit'}
        fontWeight={400}
      >
        {subtext}
      </Text>
    </Box>
  );
};

const FeaturedArticle = ({ region, lang, sectionContent }) => {
  const cards = sectionContent[1]?.cards || [];
  return (
    <Box
      maxW={{ base: '96%', md: '982px' }}
      mx={'auto'}
      m="0 auto 30px auto"
      alignItems={'center'}
      justifyContent={'center'}
      mt={{ base: '2.2rem' }}
      display="flex"
      flexWrap="wrap"
      gap={{ base: '1rem', md: '1.5rem' }}
      py={{ base: '20px', lg: '40px' }}
      px={{ base: '20px', lg: '40px' }}
      background="rgba(255, 255, 255, 0.09)"
      box-shadow="0px 42px 89.8px 0px #99C0FF0D, 0px 167px 165.4px 0px #99C0FF0D, 0px 377px 226px 0px #99C0FF0A, 0px 669px 268px 0px #99C0FF03, 0px 1046px 249.8px 0px #99C0FF00"
    >
      {cards.map((card, index) => (
        <Box
          display="flex"
          alignItems="center"
          justifyContent="center"
          key={index}
          bg="transparent"
          color={'#ffffff'}
          w={{ base: '100%', md: '100%' }}
          p={{ base: '10px', md: '10px' }}
        >
          <Box
            w="100%"
            color={'#ffffff'}
            display="flex"
            flexDirection="column"
            height="100%"
            maxW={{ lg:'600px', xl:'100%'}}
          >
            <Text
              textTransform={'uppercase'}
              {...RescPrac?.[lang]?.featureditem1}
              noOfLines={'1'}
              isTruncated={'false'}
            >
              {card.title}
            </Text>
            <Box
              width="100%"
              {...RescPrac?.[lang]?.featureditem2}
              noOfLines={'4'}
              isTruncated={'false'}
            >
              <StructuredTextParser
                str={render(card?.description)}
                region={region}
                lang={lang}
                fontSize={'18px'}
                color={'#ffffff'}
                maxW="100% !important"
                // maxW={{ base:'100%', lg:'740px', xl:'100%' }}
              />
            </Box>
            <Box
              width="100%"
              pt="0.8rem"
              {...RescPrac?.[lang]?.featureditem3}
              noOfLines={'5'}
              isTruncated={'false'}
            >
              <StructuredTextParser
                str={render(card?.moreDetailsText)}
                region={region}
                lang={lang}
                fontSize={'14px'}
                color={'#FFFFFF99'}
                mt={'0'}
                maxW="100% !important"
              />
            </Box>
            <Box
              m={{base:'0 auto', lg:'20px 0'}}
              mt="20px"
              display="flex"
              height="56px"
              w={{ base: '100%', md: '193px' }}
              alignItems={{ base:'center', lg:'flex-start'}}
              className="bottomwidget"
            >
            {card?.buttonLink && (
              <Box width="100%">
                <NextLink
                  href={refineUrlDomain(card?.buttonLink, region, lang)}
                  passHref
                  legacyBehavior
                >
                  <Link
                    textAlign="center"
                    color="#ffffff"
                    {...RescPrac?.[lang]?.featureditem4}
                    borderRadius="full"
                    border="1px solid #ffffff"
                    textTransform="uppercase"
                    target="_blank"
                    _hover={{
                      textDecoration: 'none',
                    }}
                    p="20px 20px"
                    width={{ base: '100%', md: 'auto' }}
                    display="inline-flex"
                    justifyContent="center"
                  >
                    {card?.buttonText}
                    <Image
                      w="13px"
                      h="100%"
                      ml="4px"
                      src={`${config.staticPath}/d/46272/1750039791-arrow-top-right.png`}
                    />
                  </Link>
                </NextLink>
              </Box>
            )}
          </Box>
          </Box>
        </Box>
      ))}
    </Box>
  );
};

const SliderLayout = ({ region, lang, sectionContent }) => {
  const cardsData = sectionContent;
  const isMobile = useMediaQuery({ maxWidth: 1023 });
  const [currentSlide, setCurrentSlide] = useState(0);
  const totalSlides = Math.ceil(cardsData?.cards?.length / 1);

  const CustomPrevArrow = ({ onClick }) => (
    <Box
      {...RescPrac[lang]?.sliderIcon}
      left={{ base: '-8%', lg: '-14.5%', xl: '-16%', '2xl': '-18%' }}
      cursor={currentSlide === 0 ? 'not-allowed' : 'pointer'}
      opacity={currentSlide === 0 ? 0.5 : 1}
      onClick={currentSlide === 0 ? undefined : onClick}
      display={{ base: 'none', md: 'block' }}
    >
      <Icon as={ChevronLeftIcon} boxSize={12} color="#fff" />
    </Box>
  );

  const CustomNextArrow = ({ onClick }) => (
    <Box
      {...RescPrac[lang]?.sliderIcon}
      right={{ base: '-8%', lg: '-14.5%', xl: '-16%', '2xl': '-18%' }}
      cursor={currentSlide === totalSlides - 1 ? 'not-allowed' : 'pointer'}
      opacity={currentSlide === totalSlides - 1 ? 0.5 : 1}
      onClick={currentSlide === totalSlides - 1 ? undefined : onClick}
      display={{ base: 'none', md: 'block' }}
    >
      <Icon as={ChevronRightIcon} boxSize={12} color="#fff" />
    </Box>
  );
  const settings = {
    dots: true,
    speed: 500,
    slidesToShow: 1,
    slidesToScroll: 1,
    arrows: true,
    infinite: false,
    cssEase: 'linear',
    beforeChange: (oldIndex, newIndex) => setCurrentSlide(newIndex / 1),
    prevArrow: <CustomPrevArrow />,
    nextArrow: <CustomNextArrow />,

    customPaging: i => (
      <Box
        width={{ base: '6px', lg: '11.39px' }}
        height={{ base: '6px', lg: '11.39px' }}
        borderRadius="50%"
        mt={{ base: '1rem' }}
        bgColor=" #FFFFFF33"
        transition="background-color 0.3s, opacity 0.3s"
        css={{
          '.slick-active &': {
            backgroundColor: '#EC1C2D',
            opacity: '1',
          },
        }}
        _hover={{ bgColor: '#08152D' }}
      />
    ),
  };

  return (
    <Box position="relative" top={{ base:'-30px', md:'0'}}>
      <Slider {...settings}>
        {cardsData.cards.map((card, index) => (
          <>
            <Box key={index} ml={{ base: '10px', md: '0' }} mr={{ base: '10px', md: '0' }}>
            <Box
              display="flex"
              alignItems="center"
              justifyContent="center"
              key={index}
              bg="transparent"
              color={'#ffffff'}
              p={{ base: '20px 5px', sm:'20px 10px', md: '50px 30px' }}
              maxW={'998px'}
              maxH={'405px'}
              background="rgba(255, 255, 255, 0.09)"
            >
              <Box
                w="100%"
                color={'#ffffff'}
                display="flex"
                flexDirection="column"
              >
                <Text
                  textTransform={'uppercase'}
                  {...RescPrac?.[lang]?.populararticleitem1}
                  noOfLines={'1'}
                  isTruncated={'false'}
                >
                  {card.title}
                </Text>
                <Box
                  width="100%"
                  {...RescPrac?.[lang]?.populararticleitem2}
                  noOfLines={'4'}
                  isTruncated={'false'}
                >
                  <StructuredTextParser
                    str={render(card?.description)}
                    region={region}
                    lang={lang}
                    fontSize={'18px'}
                    color={'#ffffff'}
                    maxW="100% !important"
                  />
                </Box>
                <Box
                  width="100%"
                  pt="0.8rem"
                  {...RescPrac?.[lang]?.populararticleitem3}
                  noOfLines={'5'}
                  isTruncated={'false'}
                >
                  <StructuredTextParser
                    str={render(card?.moreDetailsText)}
                    region={region}
                    lang={lang}
                    fontSize={'14px'}
                    color={'#FFFFFF99'}
                    mt={'0'}
                    maxW="100% !important"
                  />
                </Box>
                <Box
                  mt="10px"
                  display={'flex'}
                  height={'36px'}
                  alignItems={'center'}
                  className="bottomwidget"
                >
                  <Box {...RescPrac?.[lang]?.populararticleitem5}>
                    <StructuredTextParser
                      str={render(card?.additionalText)}
                      fontSize={'12px'}
                      color={'#FFFFFF'}
                      mt={'0'}
                    />
                  </Box>
                  {card?.buttonLink && (
                    <Box marginLeft={'auto'}>
                      <NextLink
                        href={refineUrlDomain(card?.buttonLink, region, lang)}
                        passHref
                        legacyBehavior
                      >
                        <Link
                          textAlign="center"
                          color="#ffffff"
                          {...RescPrac?.[lang]?.populararticleitem4}
                          borderRadius="full"
                          border={'1px solid #ffffff'}
                          textTransform={'uppercase'}
                          target="_blank"
                          _hover={{
                            textDecoration: 'none',
                          }}
                        >
                          {card?.buttonText}
                          <Image
                            w="13px"
                            h="100%"
                            ml="4px"
                            src={`${config.staticPath}/d/46272/1750039791-arrow-top-right.png`}
                          />
                        </Link>
                      </NextLink>
                    </Box>
                  )}
                </Box>
              </Box>
            </Box>
            </Box>
          </>
        ))}
      </Slider>
    </Box>
  );
};

const PopularArticle = ({ region, lang, sectionContent }) => {
  const cards = sectionContent[3]?.cards || [];
  return (
    <Box
      maxW={{ base: '900px', lg: '900px', xl: '1070px', '2xl': '1100px' }}
      pt={{ base: '40px', lg: '84px' }}
      pb={{ base: '40px', lg: '84px' }}
      px={{ base: '18px', md: '50px', xl: '62px' }}
      mx="auto"
      borderInline="none"
      display="flex"
      flexDirection="column"
      gap={{ base: '64px', md: '64px' }}
    >
      {/* headings */}
      <Box display="flex" flexDirection="column" gap="8px" textAlign={'center'}>
        <Text {...RescPrac[lang]?.title_1} color={'#ffffff'}>
          {sectionContent[2]?.titleText}
        </Text>
      </Box>
      {/* slider layout */}
      <SliderLayout
        region={region}
        lang={lang}
        sectionContent={sectionContent[3]}
      />
    </Box>
  );
};

const UpperLayout = ({ region, lang, sectionContent }) => {
  const subtext = sectionContent[0]?.additionalTextLine;

  // Extracting required data dynamically
  const titleData = sectionContent.find(
    item => item._modelApiKey === 'section_title'
  );

  return (
    <MainHeading region={region} lang={lang} sectionContent={sectionContent} />
  );
};

const LowerLayout = ({ region, lang, sectionContent }) => {
  // Extracting benefits section
  const benefitsSection = sectionContent.find(
    item => item._modelApiKey === 'card_info3_column_group'
  );
  const rearrangedBenefits = benefitsSection?.cards || [];

  return (
    <Flex
      wrap="wrap"
      mx={'auto'}
      flexDirection={{ base: 'column', md: 'row' }}
      maxW="1150px"
      mt={{ md: '2.5rem' }}
      justifyContent={{
        base: 'space-between',
        md: 'center',
        lg: 'space-between',
      }}
    >
      {rearrangedBenefits.map((item, index) => (
        <Box
          px={{ base: '1rem', md: '2rem' }}
          py={{ base: '0rem', md: '2rem' }}
          key={index}
          w={{ base: '100%', md: '30%', lg: '10%' }}
          maxW={{ base: '100%', md: 'calc(174px + 4rem)' }}
          textAlign={'left'}
          display={'flex'}
          mt={{ base: '2rem', md: '0' }}
          flexWrap={{ base: 'wrap', md: 'nowrap' }}
          alignItems={{ base: 'flex-start' }}
          justifyContent={{ base: 'center', md: 'flex-start' }}
          flexDir={{ base: 'column', md: 'column' }}
          gap={{ base: '1rem', md: '1.5rem' }}
          borderTop={{
            md: 'none',
            base:
              index === 0
                ? '1px solid transparent'
                : '1px solid rgba(255, 255, 255, 0.20)',
          }}
          borderRight={{
            base: 'none',
            md:
              rearrangedBenefits.length - 1 == index
                ? '0'
                : '1px solid #FFFFFF33',
          }}
          _last={{ borderBottom: 'none' }}
          flex={{ base: 1, md: '30%', lg: 1 }}
        >
          {/* Icon box */}
          <Box flex={1}>
            <LazyLoadImageComponent
              w="130px"
              h="130px"
              objectFit="cover"
              src={item.thumbnail?.url}
              alt={item.thumbnail?.alt || 'Benefit image'}
            />
            <Text
              mt={{ base: '12px', md: '1.5rem' }}
              maxW={{ lg: '165px' }}
              px={{ base: '0', md: '0' }}
              {...RescPrac?.[lang]?.item6}
            >
              {item.title}
            </Text>
          </Box>

          {/* Title */}
        </Box>
      ))}
    </Flex>
  );
};

const BannerResearch = ({ region, lang, sectionContent }) => {
  return (
    <Box pt={{ base: '0', md: '5rem' }} mb={{ base:'60px', lg:'0' }}>
      {/* upper layout */}
      <Box>
        <UpperLayout
          region={region}
          lang={lang}
          sectionContent={sectionContent}
        />
      </Box>
      {/* <FeaturedArticle
        region={region}
        lang={lang}
        sectionContent={sectionContent}
      />

      <PopularArticle
        region={region}
        lang={lang}
        sectionContent={sectionContent}
      /> */}

      {/* Lower layout --Benefits Section */}
      {/* <LowerLayout
        region={region}
        lang={lang}
        sectionContent={sectionContent}
      /> */}
    </Box>
  );
};

export default BannerResearch;
